import React, { useCallback, useEffect, useRef } from "react";
import { SendIcon, StopIcon } from "../icons";

const MAX_HEIGHT = 120;

/** Must match ChatAgent:MaxUserMessageLength on ChatMS, which 400s above it. */
const MAX_MESSAGE_LENGTH = 4000;

/**
 * Message input. Enter sends, Shift+Enter adds a newline - the convention
 * billers already know from every other chat tool.
 */
export default function Composer({ value, onChange, onSubmit, onStop, busy, disabled }) {
  const textareaRef = useRef(null);

  // Auto-grow to fit content, capped so the transcript never gets squeezed out.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`;
  }, [value]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        if (!busy && value.trim()) onSubmit();
      }
    },
    [busy, value, onSubmit]
  );

  return (
    <div className="remi-composer">
      <textarea
        ref={textareaRef}
        className="remi-input"
        rows={1}
        value={value}
        disabled={disabled}
        /* ChatMS rejects anything longer with a 400. Capping here means a biller pasting a
           long denial note finds out immediately instead of after a round trip. */
        maxLength={MAX_MESSAGE_LENGTH}
        placeholder="Ask about a claim, denial, or AR..."
        aria-label="Ask Remi a question"
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
      />

      {busy ? (
        <button
          type="button"
          className="remi-send remi-send-stop"
          onClick={onStop}
          title="Stop"
          aria-label="Stop generating"
        >
          <StopIcon />
        </button>
      ) : (
        <button
          type="button"
          className="remi-send"
          onClick={onSubmit}
          disabled={disabled || !value.trim()}
          title="Send"
          aria-label="Send message"
        >
          <SendIcon />
        </button>
      )}
    </div>
  );
}
